import { RadioGroup, RadioGroupItem } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useBespokeRadioRowGroup } from './BespokeRadioRowContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { joinClasses, BESPOKE_RADIO_ITEM_CLASS } from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISRadioOptionProps {
  qid: string;
  value: string;
  className?: string;
  style?: React.CSSProperties;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  itemClassName?: string;
}

/**
 * A single selectable option for a radio QID.  Multiple OASISRadioOption nodes
 * sharing the same qid form one radio group — useful for placing radio inputs
 * inside table cells or other arbitrary layouts.
 */
export function OASISRadioOption({
  qid,
  value,
  className,
  style,
  controlClassName,
  itemClassName,
}: OASISRadioOptionProps) {
  const inRowGroup = useBespokeRadioRowGroup();
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);
  const selectedValue = (answers[qid] as string) ?? '';
  const isChecked = selectedValue === value;

  const handleDeselectClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isChecked) return;
    event.preventDefault();
    event.stopPropagation();
    onAnswerChange(qid, '');
  };

  const item = (
    <RadioGroupItem
      value={value}
      disabled={inheritedDisabled}
      className={joinClasses(
        BESPOKE_RADIO_ITEM_CLASS,
        'shrink-0',
        inheritedDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
        resolveControlClassName(controlClassName, itemClassName)
      )}
      {...(inheritedDisabled ? {} : { onClick: handleDeselectClick })}
    />
  );

  const wrapperClassName = className ?? 'flex justify-center items-center';

  if (inRowGroup) {
    return (
      <div data-qid={qid} className={wrapperClassName} style={style}>
        {item}
      </div>
    );
  }

  return (
    <div data-qid={qid} className={wrapperClassName} style={style}>
      <RadioGroup
        value={selectedValue}
        onValueChange={next => {
          if (inheritedDisabled) return;
          onAnswerChange(qid, next);
        }}
        disabled={inheritedDisabled}
      >
        {item}
      </RadioGroup>
    </div>
  );
}
